/**
 * Typed artefact API helpers — Track 2.G.
 *
 * Thin wrappers around `ApiClient` for artefact-instance operations.
 *
 * Architecture refs:
 *   functionality.md §3 — artefact domain model (lines 109–124)
 *   cook-as-process-design §2.4 — lifecycle + observation log
 *   architecture.md §4.7 — DDL: artefacts, artefact_slug_history,
 *                          artefact_observation_bodies
 *   architecture.md §4.23 — server-stamped author_agent; NOT accepted from client
 *
 * Security invariant: `authorAgent` is never accepted from client input.
 */

import type { ApiClient } from "./index.js";

// ── Row types ─────────────────────────────────────────────────────────────────

export interface ArtefactRow {
  uuid: string;
  slug: string;
  name: string;
  kind_uuid: string;
  parent_artefact_uuid: string | null;
  origin_attempt_uuid: string | null;
  /** 'active' | 'dormant' | 'retired' */
  status: string;
  created_at: number;
  updated_at: number;
}

export interface ArtefactObsBodyRow {
  artefact_uuid: string;
  body_markdown: string;
  /** The sync-DO y-doc key: obs-artefact:<uuid> */
  body_doc_uuid: string;
  updated_at: number;
}

export interface ArtefactSlugHistoryEntry {
  artefact_uuid: string;
  slug: string;
  retired_at: number;
}

// ── Input types ───────────────────────────────────────────────────────────────

export interface ArtefactCreateInput {
  slug: string;
  name: string;
  kindUuid: string;
  parentArtefactUuid?: string | null;
  originAttemptUuid?: string | null;
}

export interface ArtefactUpdateInput {
  uuid: string;
  slug?: string;
  name?: string;
  status?: "active" | "dormant" | "retired";
}

export interface ArtefactListInput {
  kindUuid?: string | null;
  status?: "active" | "dormant" | "retired" | null;
  limit?: number;
  cursor?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Create a new artefact instance.
 *
 * The server creates the observation-body row automatically.
 * The obs-log doc key is `obs-artefact:<returned-uuid>` — use it to
 * open a sync-DO WebSocket for the observation log.
 *
 * @throws `ORPCError("CONFLICT")` if slug is taken.
 * @throws `ORPCError("NOT_FOUND")` if kindUuid or parentArtefactUuid not found.
 */
export async function createArtefact(
  api: ApiClient,
  input: ArtefactCreateInput,
): Promise<ArtefactRow> {
  return (await api.artefact.create(input)) as ArtefactRow;
}

/**
 * Fetch a single artefact by UUID.
 *
 * @throws `ORPCError("NOT_FOUND")` if unknown.
 */
export async function getArtefact(api: ApiClient, uuid: string): Promise<ArtefactRow> {
  return (await api.artefact.get({ uuid })) as ArtefactRow;
}

/**
 * List artefacts with optional kind/status filter and keyset pagination.
 */
export async function listArtefacts(
  api: ApiClient,
  input: ArtefactListInput = {},
): Promise<{ artefacts: ArtefactRow[]; nextCursor: string | null }> {
  return (await api.artefact.list(input)) as {
    artefacts: ArtefactRow[];
    nextCursor: string | null;
  };
}

/**
 * Update an artefact's slug, name, or status.
 *
 * Status transitions: active ↔ dormant, active → retired, dormant → retired.
 * On active → dormant | retired, close the obs-log sync-DO WebSocket and
 * trigger the freeze via the sync commit path before or after this call.
 *
 * @throws `ORPCError("CONFLICT")` on invalid status transition or slug conflict.
 */
export async function updateArtefact(
  api: ApiClient,
  input: ArtefactUpdateInput,
): Promise<ArtefactRow> {
  return (await api.artefact.update(input)) as ArtefactRow;
}

/**
 * Hard-delete an artefact.
 *
 * @throws `ORPCError("NOT_FOUND")` if unknown.
 */
export async function deleteArtefact(api: ApiClient, uuid: string): Promise<{ ok: boolean }> {
  return (await api.artefact.delete({ uuid })) as { ok: boolean };
}

/**
 * List retired slugs for an artefact (for /u/<handle>/a/<slug> redirect resolution).
 */
export async function listArtefactSlugHistory(
  api: ApiClient,
  uuid: string,
): Promise<{ history: ArtefactSlugHistoryEntry[] }> {
  return (await api.artefact.slugHistory({ uuid })) as {
    history: ArtefactSlugHistoryEntry[];
  };
}

/**
 * Get the artefact observation-body row (frozen markdown + live doc key).
 *
 * While the artefact is `active`, the `body_doc_uuid` key hosts the live
 * CRDT y-doc in the sync-DO.
 */
export async function getArtefactObsBody(
  api: ApiClient,
  uuid: string,
): Promise<ArtefactObsBodyRow> {
  return (await api.artefact.getObsBody({ uuid })) as ArtefactObsBodyRow;
}
